// Filename: ModernSlaveryPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const ModernSlaveryPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <nav className="bg-white shadow-md fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center">
                    <Link to="/" className="flex items-center">
                        <img
                            src="/appsytec-logo.png"
                            alt="Appsytec Logo"
                            className="h-10 w-auto"
                        />
                    </Link>
                    <Link to="/" className="text-gray-700 font-medium hover:text-red-500 transition-colors">
                        Home
                    </Link>
                </div>
            </nav>

            <main className="flex-grow pt-24 pb-20">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none">
                        <h1 className="text-4xl font-bold text-gray-900 mb-8">Modern Slavery Statement</h1>

                        <div className="space-y-8">
                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    This statement is made pursuant to the Modern Slavery Act 2018 (Cth) and sets out the steps that Appsytec Pty Ltd (ABN: 68 672 939 258) takes on an ongoing basis to ensure that modern slavery is not taking place in our business operations or supply chains.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">2. About Appsytec</h2>
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Structure</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Appsytec is an Australian proprietary limited company incorporated and operating in Australia. We are a boutique ICT recruitment and consulting firm headquartered in Melbourne, Victoria.
                                </p>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Operations</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Our core business activities include:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>ICT workforce augmentation and labour hire services</li>
                                    <li>Permanent ICT recruitment for government and private sector clients</li>
                                    <li>Technical consulting and web development services</li>
                                    <li>Workforce planning consultancy</li>
                                </ul>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Supply Chain</h3>
                                <p className="text-gray-700 leading-relaxed">
                                    As a professional services firm, our supply chain is relatively simple and consists primarily of:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Technology service providers (software licensing, cloud services)</li>
                                    <li>Professional services (legal, accounting, insurance)</li>
                                    <li>Office supplies and equipment</li>
                                    <li>Marketing and advertising services</li>
                                    <li>Independent contractors and consultants</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Modern Slavery Risks</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We recognise that modern slavery can occur in various forms, including forced labour, debt bondage, forced marriage, slavery, and human trafficking. Given our business model and operations, we have assessed our modern slavery risks as low, but we remain vigilant in the following areas:
                                </p>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Supply Chain Risks</h3>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                                    <li>Technology hardware suppliers that may have complex global supply chains</li>
                                    <li>Cleaning and facilities management services</li>
                                    <li>Promotional merchandise and office supplies from overseas manufacturers</li>
                                </ul>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Operational Risks</h3>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Ensuring fair employment practices for all contractors and employees</li>
                                    <li>Verifying the legitimacy of independent contractors and their employment practices</li>
                                    <li>Ensuring our clients maintain ethical employment practices</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Actions Taken to Address Modern Slavery</h2>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Policies and Governance</h3>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We have:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                                    <li>Developed and implemented a Modern Slavery Policy</li>
                                    <li>Integrated modern slavery considerations into our supplier code of conduct</li>
                                    <li>Updated our recruitment and onboarding processes to include modern slavery awareness</li>
                                    <li>Established clear reporting mechanisms for suspected modern slavery incidents</li>
                                </ul>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Due Diligence</h3>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-4">
                                    <li>Enhanced supplier assessment processes to include modern slavery risk evaluation</li>
                                    <li>Implemented additional verification procedures for independent contractors</li>
                                    <li>Conducted risk assessments of our major suppliers and service providers</li>
                                </ul>
                                
                                <h3 className="text-xl font-semibold text-gray-900 mb-3">Training and Awareness</h3>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Provided modern slavery awareness training to all staff</li>
                                    <li>Educated our recruitment team on indicators of forced labour</li>
                                    <li>Established regular review processes for modern slavery compliance</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Assessing Effectiveness</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    We measure the effectiveness of our actions through:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Regular review of supplier assessments and compliance</li>
                                    <li>Monitoring of grievance and reporting mechanisms</li>
                                    <li>Annual review of policies and procedures</li>
                                    <li>Training completion rates and feedback</li>
                                    <li>External audit and compliance reviews where appropriate</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Consultation Process</h2>
                                <p className="text-gray-700 leading-relaxed">
                                    As Appsytec Pty Ltd does not own or control any other entities, no consultation with subsidiary entities was required for this statement. This statement was prepared in consultation with our senior management team and approved by our board of directors.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Looking Forward</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    Looking ahead, we commit to:
                                </p>
                                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                                    <li>Continuing to enhance our supplier assessment processes</li>
                                    <li>Expanding modern slavery awareness training across our network</li>
                                    <li>Strengthening our monitoring and reporting mechanisms</li>
                                    <li>Collaborating with industry peers to share best practices</li>
                                    <li>Regularly reviewing and updating our policies and procedures</li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Contact Information</h2>
                                <p className="text-gray-700 leading-relaxed mb-4">
                                    If you have any questions about this statement or wish to report concerns related to modern slavery, please contact us:
                                </p>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <p className="text-gray-700"><strong>Appsytec Pty Ltd</strong></p>
                                    <p className="text-gray-700">Email: contact@appsytec.com</p>
                                    <p className="text-gray-700">Location: Melbourne, Australia</p>
                                </div>
                            </section>

                            {/* <section className="border-t pt-8">
                                <p className="text-gray-600 text-sm">
                                    This statement was approved by the board of directors of Appsytec Pty Ltd on [Date] and is signed by a responsible member pursuant to section 14 of the Modern Slavery Act 2018 (Cth).
                                </p>
                                <div className="mt-8 p-4 border border-gray-300 rounded-lg">
                                    <p className="text-gray-700 mb-2"><strong>Signature:</strong> _________________</p>
                                    <p className="text-gray-700 mb-2"><strong>Name:</strong> [Director Name]</p>
                                    <p className="text-gray-700 mb-2"><strong>Position:</strong> Director</p>
                                    <p className="text-gray-700"><strong>Date:</strong> [Date]</p>
                                </div>
                            </section> */}
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-red-500 rounded-full"></div>
                            <span className="ml-2 text-xl font-bold">Appsytec Pty Ltd</span>
                        </div>
                        <p className="text-gray-400 mb-4">&copy; {new Date().getFullYear()} Appsytec Pty Ltd. All rights reserved.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ModernSlaveryPage;